import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddToCartDimensions, AnalyticsEvent, logAnalyticsEvent } from '@vestico/utils';

import { ClientReviewResponse, ProductWidget } from '@vestico/model';

import { GlobalsContext } from '@vestico/shared';

import { ConfigContext } from '../WidgetEventsManager';

export type AddToBagStatus = 'none' | 'loadingVariants' | 'loadingVariantsError' | 'addingToBag' | 'addToBagSuccess' | 'addToBagError';

export type AddToBagSize = 'default' | 'small';
export type AddToBagColorScheme = 'light' | 'dark';

export interface AddToBagVariant {
  title: string;
  id: string;
  available: boolean;
}

export interface AddToBagProps {
  product?: ProductWidget | null;
  response?: ClientReviewResponse | null;
}

export const useAddToBagActions = ({ response, product }: AddToBagProps) => {
  const { stylesConfig } = useContext(GlobalsContext);
  const { addToCartConfig } = stylesConfig.stylesConfig;
  const addToBagEnabled = useMemo(() => addToCartConfig && addToCartConfig.enabled && product !== null, [addToCartConfig, product]);
  const { clientId } = useContext(ConfigContext);
  const {
    getAvailableSizesScript: getAvailableVariantsScript,
    addToCartScript,
    goToCartURL,
    reloadPage,
  } = addToCartConfig ?? { getAvailableSizesScript: '', addToCartScript: '' };
  const goToCartEnabled = useMemo(() => !!goToCartURL && goToCartURL?.length > 0, [goToCartURL]);

  const { t } = useTranslation();

  const [addToBagStatus, setAddToBagStatus] = useState<AddToBagStatus>('loadingVariants');
  const [addToBagError, setAddtoBagError] = useState<string | undefined>(undefined);
  const [availableAddToBagVariants, setAvailableAddToBagVariants] = useState<AddToBagVariant[] | undefined>(undefined);

  const loadAvailableVariants = useCallback(async () => {
    if (!product || !getAvailableVariantsScript || !addToBagEnabled) {
      return;
    }

    const waitFn = (ev: string) => {
      return new Promise((resolve) => {
        try {
          // eslint-disable-next-line no-new-func
          new Function('resolve', 'product', ev)(resolve, product);
        } catch (error) {
          console.error(error);
          logAnalyticsEvent({ category: 'exception', event: 'get-add-to-cart-sizes', name: error.name });
          resolve(undefined);
        }
      });
    };

    setAddToBagStatus('loadingVariants');

    // For Testing
    // setAvailableAddToBagVariants([
    //   { title: '', available: true, id: '41933277167783' },
    //   { title: 'M', available: true, id: '41933277200551' },
    //   { title: 'L', available: false, id: '41933277233319' },
    // ]);
    // setAddToBagStatus('none');

    const availableOptions = (await waitFn(getAvailableVariantsScript)) as AddToBagVariant[] | undefined;

    setAvailableAddToBagVariants(availableOptions);

    if (!availableOptions || availableOptions.length === 0) {
      setAddToBagStatus('loadingVariantsError');
    } else {
      setAddToBagStatus('none');
    }
  }, [addToBagEnabled, getAvailableVariantsScript, product]);

  const addToBag = useCallback(
    async (variant: AddToBagVariant) => {
      if (!product || !addToBagEnabled || !addToCartScript) {
        return;
      }

      const waitFn = (ev: string) => {
        return new Promise((resolve) => {
          try {
            // eslint-disable-next-line no-new-func
            new Function('resolve', 'size', ev)(resolve, variant);
          } catch (error) {
            console.error(error);
            logAnalyticsEvent({ category: 'exception', event: 'add-to-cart', name: error.name });
            resolve(false);
          }
        });
      };

      console.info(`Add to bag: ${product?.uid}, ${variant.title}`);

      const dimensions: AddToCartDimensions = {
        post_id: response?.postDocumentID,
        post_media_type: response?.media.mainMimeType,
        provider: clientId,
        item_id: product.documentID,
        name: product.name,
        brand: product.brandName,
        items: [
          {
            item_id: product.documentID,
            item_name: product.name,
            item_brand: product.brandName,
            item_variant: variant.title,
          },
        ],
      };
      logAnalyticsEvent({ category: 'event', event: AnalyticsEvent.addToCart, dimensions });

      setAddToBagStatus('addingToBag');

      // For Testing
      // const success = await waitFn('setTimeout(() => resolve(true), 2000)');

      // eslint-disable-next-line no-new-func
      const success = await waitFn(addToCartScript);

      if (!success) {
        logAnalyticsEvent({ category: 'exception', event: 'add-to-cart-failed' });
      }

      setAddToBagStatus(success ? 'addToBagSuccess' : 'addToBagError');
      setAddtoBagError(success ? undefined : t('LOC_ADD_TO_BAG_ADD_ERROR'));
    },
    [product, response, addToBagEnabled, addToCartScript, clientId, t]
  );

  const goToCart = useCallback(() => {
    if (!goToCartURL || !goToCartEnabled) {
      return;
    }
    document.location.href = goToCartURL;
  }, [goToCartEnabled, goToCartURL]);

  return {
    addToBag,
    loadAvailableVariants,
    addToBagStatus,
    addToBagEnabled,
    addToBagError,
    availableAddToBagVariants,
    goToCartEnabled,
    goToCart,
    reloadPage,
  };
};
